import { api, ApiError } from '@/lib/api';
import type { SourceCoverage } from '@waypoint/shared';

/**
 * Every figure is null when the source it comes from is not connected and synced
 * (`coverage`, apps/api domain/integrations/surface-coverage.ts): null is "Waypoint can't
 * see that", never zero.
 */
export interface RevenueForecast {
  coverage: SourceCoverage;
  revenueCollected: number | null;
  projectedRevenue: number | null;
  revenueGoal: number | null;
  pipelineRaw: number | null;
  onTrack: boolean | null;
  narrative: string | null;
  weeklyCloseRateNeeded: number | null;
  scenarios: { pessimistic: number; likely: number; optimistic: number } | null;
}

const NO_SOURCES: SourceCoverage = { crm: false, money: false, mailbox: false, contact: false };

/** Returned when the endpoint 404s: nothing is visible, so nothing is claimed. */
const NOT_CONNECTED: RevenueForecast = {
  coverage: NO_SOURCES,
  revenueCollected: null,
  projectedRevenue: null,
  revenueGoal: null,
  pipelineRaw: null,
  onTrack: null,
  narrative: null,
  weeklyCloseRateNeeded: null,
  scenarios: null,
};

export async function getRevenueForecast(): Promise<RevenueForecast> {
  try {
    return await api('/v1/revenue-forecast');
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return NOT_CONNECTED;
    throw err;
  }
}
